/**
 * useOperatorReports — B.11 «Отчёты» list data.
 *
 * Three buckets:
 *   • need — games still owing a report, from GET /assignments/my-problems
 *     ?date= (problem_type=missing). That endpoint already excludes
 *     cancelled/archived bookings and games already reported, and only
 *     surfaces games that have actually ended — so a submitted report never
 *     "bounces back" into need.
 *   • fix  — own submitted reports flagged needs_correction (OO bounce-back).
 *   • done — own submitted reports (needs_correction excluded → shown in fix).
 * game-reports?date= is operator-scoped server-side (own reports only).
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "../api/client";
import type { ApiList } from "../api/types";
import { fmtDateApi } from "../utils/date";

export interface OpUnreported {
  id: string;
  bookingId: number;
  time: string;
  questName: string;
  shortName: string;
  players: number;
  clientName: string | null;
  source: string;
  tea: boolean;
  teaRoomId: string | null;
  mc: string | null;
  branchId: string | null;
}

export interface OpReport {
  id: number;
  bookingId: number;
  date: string;
  time: string;
  questName: string;
  shortName: string;
  isPrimary: boolean;
  players: number;
  total: number;
  expectedTotal: number;
  cash: number;
  terminal: number;
  transfer: number;
  prepaidUsed: number;
  invoice: number;
  activity: string;
  comment: string;
  ooComment: string;
  needsCorrection: boolean;
  correctionReason: string;
  source: string;
  tea: boolean;
  teaRoomId: string | null;
  mc: string | null;
  clientName: string | null;
  branchId: string | null;
}

interface RawProblem {
  problem_type?: string;
  assignment_id?: string | number;
  report_id?: number;
  booking_id: number;
  time?: string;
  custom_time?: string | null;
  short_name?: string;
  quest_name?: string;
  players?: number;
  source?: string;
  tea?: number | boolean;
  tea_room_id?: string | null;
  mc?: string | null;
  branch_id?: string | null;
}

interface RawReport {
  id: number;
  booking_id: number;
  date?: string;
  time?: string;
  quest_name?: string;
  short_name?: string;
  is_primary?: number;
  actual_players?: number;
  players_actual?: number;
  booked_players?: number;
  total?: number;
  expected_total?: number;
  cash?: number;
  terminal?: number;
  transfer?: number;
  prepaid_used?: number;
  invoice?: number;
  activity?: string;
  comment?: string;
  oo_comment?: string;
  needs_correction?: number | boolean;
  correction_reason?: string;
  source?: string;
  tea?: number | boolean;
  tea_room_id?: string | null;
  booking_tea_room_id?: string | null;
  mc?: string | null;
  client_name?: string | null;
  branch_id?: string | null;
}

export interface OperatorReportsResult {
  need: OpUnreported[];
  fix: OpReport[];
  done: OpReport[];
  loading: boolean;
  error: string | null;
  reload: () => void;
}

function mapReport(r: RawReport): OpReport {
  return {
    id: r.id,
    bookingId: r.booking_id,
    date: r.date ?? "",
    time: (r.time ?? "").slice(0, 5),
    questName: r.quest_name ?? r.short_name ?? "—",
    shortName: r.short_name ?? r.quest_name ?? "—",
    isPrimary: !!r.is_primary,
    players: Number(r.actual_players ?? r.players_actual ?? r.booked_players ?? 0),
    total: Number(r.total ?? 0),
    expectedTotal: Number(r.expected_total ?? 0),
    cash: Number(r.cash ?? 0),
    terminal: Number(r.terminal ?? 0),
    transfer: Number(r.transfer ?? 0),
    prepaidUsed: Number(r.prepaid_used ?? 0),
    invoice: Number(r.invoice ?? 0),
    activity: r.activity ?? "",
    comment: r.comment ?? "",
    ooComment: r.oo_comment ?? "",
    needsCorrection: !!r.needs_correction,
    correctionReason: r.correction_reason ?? "",
    source: r.source ?? "",
    tea: !!r.tea,
    teaRoomId: r.tea_room_id ?? r.booking_tea_room_id ?? null,
    mc: r.mc ?? null,
    clientName: r.client_name ?? null,
    branchId: r.branch_id ?? null,
  };
}

export function useOperatorReports(date: Date, operatorId?: string): OperatorReportsResult {
  const [need, setNeed] = useState<OpUnreported[]>([]);
  const [fix, setFix] = useState<OpReport[]>([]);
  const [done, setDone] = useState<OpReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [trigger, setTrigger] = useState(0);
  const abortRef = useRef<AbortController | null>(null);
  const reload = useCallback(() => setTrigger((n) => n + 1), []);

  useEffect(() => {
    abortRef.current?.abort();
    const ac = new AbortController();
    abortRef.current = ac;
    setLoading(true);
    setError(null);
    const dateStr = fmtDateApi(date);

    (async () => {
      try {
        const probRes = await api<ApiList<RawProblem>>(`/api/assignments/my-problems?date=${dateStr}`, { signal: ac.signal });
        const missing = (probRes.results ?? []).filter((p) => p.problem_type === "missing");

        let reports: OpReport[] = [];
        try {
          const rRes = await api<ApiList<RawReport>>(`/api/game-reports?date=${dateStr}&mine=1`, { signal: ac.signal });
          reports = (rRes.results ?? []).map(mapReport);
        } catch {
          /* reports endpoint unavailable → keep need-bucket only */
        }
        if (ac.signal.aborted) return;

        const unreported: OpUnreported[] = missing.map((p) => ({
          id: String(p.assignment_id ?? p.booking_id),
          bookingId: p.booking_id,
          time: (p.custom_time || p.time || "").slice(0, 5),
          questName: p.quest_name ?? p.short_name ?? "—",
          shortName: p.short_name ?? p.quest_name ?? "—",
          players: Number(p.players ?? 0),
          clientName: null,
          source: p.source ?? "",
          tea: !!p.tea,
          teaRoomId: p.tea_room_id ?? null,
          mc: p.mc ?? null,
          branchId: p.branch_id ?? null,
        }));

        setNeed(unreported);
        setFix(reports.filter((r) => r.needsCorrection));
        setDone(reports.filter((r) => !r.needsCorrection));
        setLoading(false);
      } catch (e: unknown) {
        if (ac.signal.aborted) return;
        setError(e instanceof Error ? e.message : "Не удалось загрузить отчёты");
        setLoading(false);
      }
    })();

    return () => ac.abort();
  }, [date, operatorId, trigger]);

  return { need, fix, done, loading, error, reload };
}
